import { Link } from "react-router-dom";
import { Phone, Mail, Clock, ArrowRight, Instagram, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import logoAsset from "@/assets/logo-villalobos.png.asset.json";


const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-accent text-accent-foreground">
      {/* CTA Banner */}
      <div className="bg-primary relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="container-max px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready to Start Your Project?
              </h2>
              <p className="text-primary-foreground/80 text-sm mt-1">
                Free estimates across Deltona and Central Florida — new clients save 10%.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Started <ArrowRight className="h-5 w-5" />
              </Button>
              <a href="tel:7863065704">
                <Button
                  variant="secondary"
                  size="lg"
                  className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
                >
                  <Phone className="h-4 w-4" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-white p-3 shadow-premium">
              <img src={logoAsset.url} alt="Villalobos Prime Services LLC" className="h-24 w-auto rounded-xl" />
            </div>
            <p className="text-accent-foreground/75 text-sm leading-relaxed">
              Villalobos Prime Services LLC — painting, flooring, drywall, and remodeling in Deltona, FL. Quality Work. Honest Service. Lasting Results.
            </p>
            <div className="mt-4 bg-secondary/10 border border-secondary/20 rounded-xl px-4 py-3">
              <p className="text-secondary font-semibold text-sm">
                🎁 New clients get 10% OFF their first project!
              </p>
            </div>
            <a
              href="https://www.instagram.com/villalobos.primeservices"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram @villalobos.primeservices"
              className="mt-4 inline-flex items-center gap-2 text-accent-foreground/75 hover:text-secondary transition-colors text-sm font-semibold"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 border border-white/15">
                <Instagram className="h-4 w-4" />
              </span>
              @villalobos.primeservices
            </a>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: "Services", to: "/services" },
                { label: "Locations", to: "/locations" },
                { label: "Gallery", to: "/gallery" },
                { label: "Blog", to: "/blog" },
                { label: "About", to: "/about" },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="footer-link-underline text-accent-foreground/75 hover:text-secondary text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary" />
                <a href="tel:7863065704" className="hover:text-secondary transition-colors">(786) 306-5704</a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <span>
                  <a href="tel:3863209362" className="hover:text-secondary transition-colors">(386) 320-9362</a>
                  <span className="block text-xs text-accent-foreground/60">English customer attention</span>
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary" />
                <a href="mailto:villalobosprime.services@gmail.com" className="hover:text-secondary transition-colors break-all">villalobosprime.services@gmail.com</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary" />
                Deltona, FL
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Hours
            </h3>
            <ul className="space-y-2 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary" />
                Monday – Friday
              </li>
              <li className="pl-6">8:00 AM – 7:00 PM</li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary" />
                Saturday
              </li>
              <li className="pl-6">8:00 AM – 4:00 PM</li>
            </ul>
            <div className="mt-4 space-y-1">
              <p className="text-accent-foreground/65 text-xs">Serving Volusia, Seminole & Orange Counties</p>
              <p className="text-accent-foreground/65 text-xs">Free estimates · 10% new client discount</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-accent-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-accent-foreground/60 text-xs">
            © {new Date().getFullYear()} Villalobos Prime Services LLC. All rights reserved.
          </p>
          <span className="text-accent-foreground/60 text-xs">Quality Work · Honest Service · Lasting Results</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
